import { useEffect, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMoneyBill,
  faChartPie,
  faChartLine,
  faCalendarWeek,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import { useAuth } from "../../../Auths/CheckUser";

const AirArrears = () => {
  const { user } = useAuth();
  const [totalArrears, setTotalArrears] = useState(0);
  const [activeArrears, setActiveArrears] = useState(0);
  const [nonActiveArrears, setNonActiveArrears] = useState(0);
  const [weeklyArrears, setWeeklyArrears] = useState({ active: 0, nonActive: 0 });
  const [monthlyArrears, setMonthlyArrears] = useState({ active: 0, nonActive: 0 });

  // Fetch staff clients
  useEffect(() => {
    if (!user || !user.id) return;

    const fetchStaffClients = async () => {
      try {
        const res = await axios.get(`https://bpohasil.ngrok.app/api/user/${user.id}`);
        const staffData = res.data;

        const clientList = Array.isArray(staffData.airclients) ? staffData.airclients : [];
        calculateArrearsTotals(clientList);
        calculateWeeklyMonthly(clientList);
      } catch (err) {
        console.error("Error fetching staff clients:", err);
      }
    };

    fetchStaffClients();
  }, [user]);

  // Calculate overall totals
  const calculateArrearsTotals = (records) => {
    let total = 0;
    let active = 0;
    let nonActive = 0;

    records.forEach((item) => {
      const arrears = item.arrears || 0;
      total += arrears;

      if (item.status === "AKTIF" || item.status === "Active") active += arrears;
      else nonActive += arrears;
    });

    setTotalArrears(total);
    setActiveArrears(active);
    setNonActiveArrears(nonActive);
  };

  // Calculate ongoing week and month arrears
  const calculateWeeklyMonthly = (records) => {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    const endOfWeek = new Date(now);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    let weekly = { active: 0, nonActive: 0 };
    let monthly = { active: 0, nonActive: 0 };

    records.forEach((item) => {
      if (!item.createdAt) return;
      const date = new Date(item.createdAt);
      const arrears = item.arrears || 0;

      if (date >= startOfWeek && date <= endOfWeek) {
        if (item.status === "AKTIF" || item.status === "Active")
          weekly.active += arrears;
        else weekly.nonActive += arrears;
      }

      if (date >= startOfMonth && date <= endOfMonth) {
        if (item.status === "AKTIF" || item.status === "Active")
          monthly.active += arrears;
        else monthly.nonActive += arrears;
      }
    });

    setWeeklyArrears(weekly);
    setMonthlyArrears(monthly);
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      <div className="text-center mb-5">
        <h3 className="text-xl font-bold text-gray-800">Rent Arrears Report</h3>
        <p className="text-gray-500 text-sm">Staff: {user?.name}</p>
      </div>

      {/* Total Arrears Card */}
      <div className="bg-gradient-to-r from-red-500 to-rose-600 p-4 rounded-xl shadow-md mb-6 text-white hover:scale-[1.01] transition-transform duration-300">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium">Total Rent Arrears</h3>
            <p className="text-2xl font-bold mt-1">RM {totalArrears.toFixed(2)}</p>
          </div>
          <div className="bg-white/20 p-3 rounded-full">
            <FontAwesomeIcon icon={faMoneyBill} className="text-white text-xl" />
          </div>
        </div>
      </div>

      {/* Overall Arrears Summary */}
      <div className="bg-white p-4 rounded-xl shadow-md">
        <div className="flex items-center mb-4">
          <FontAwesomeIcon icon={faChartPie} className="text-red-600 text-lg mr-2" />
          <h3 className="text-lg font-bold text-gray-800">Overall Arrears Summary</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link to="/arrears-air-actives">
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg hover:shadow-md transition-shadow duration-300">
              <div className="flex justify-between items-center">
                <div>
                  <h4 className="text-green-700 font-semibold text-base">Active</h4>
                  <p className="text-xl font-bold mt-1 text-gray-800">
                    RM {activeArrears.toFixed(2)}
                  </p>
                </div>
                <div className="bg-green-100 p-3 rounded-full">
                  <FontAwesomeIcon icon={faChartLine} className="text-green-600 text-lg" />
                </div>
              </div>
            </div>
          </Link>

          <Link to="/arrears-air-nonactives">
            <div className="bg-red-50 border border-red-200 p-4 rounded-lg hover:shadow-md transition-shadow duration-300">
              <div className="flex justify-between items-center">
                <div>
                  <h4 className="text-red-700 font-semibold text-base">Non-Active</h4>
                  <p className="text-xl font-bold mt-1 text-gray-800">
                    RM {nonActiveArrears.toFixed(2)}
                  </p>
                </div>
                <div className="bg-red-100 p-3 rounded-full">
                  <FontAwesomeIcon icon={faMoneyBill} className="text-red-600 text-lg" />
                </div>
              </div>
            </div>
          </Link>
        </div>
      </div>

      {/* Weekly Report */}
      <div className="bg-white p-4 rounded-xl shadow-md mt-6">
        <div className="flex items-center mb-4">
          <FontAwesomeIcon icon={faCalendarWeek} className="text-blue-600 text-lg mr-2" />
          <h3 className="text-lg font-bold text-gray-800">Weekly Arrears Report</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h4 className="text-green-700 font-semibold text-base">Active</h4>
            <p className="text-xl font-bold mt-1 text-gray-800">
              RM {weeklyArrears.active.toFixed(2)}
            </p>
          </div>
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <h4 className="text-red-700 font-semibold text-base">Non-Active</h4>
            <p className="text-xl font-bold mt-1 text-gray-800">
              RM {weeklyArrears.nonActive.toFixed(2)}
            </p>
          </div>
        </div>
      </div>

      {/* Monthly Report */}
      <div className="bg-white p-4 rounded-xl shadow-md mt-6">
        <div className="flex items-center mb-4">
          <FontAwesomeIcon icon={faCalendarAlt} className="text-purple-600 text-lg mr-2" />
          <h3 className="text-lg font-bold text-gray-800">Monthly Arrears Report</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h4 className="text-green-700 font-semibold text-base">Active</h4>
            <p className="text-xl font-bold mt-1 text-gray-800">
              RM {monthlyArrears.active.toFixed(2)}
            </p>
          </div>
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <h4 className="text-red-700 font-semibold text-base">Non-Active</h4>
            <p className="text-xl font-bold mt-1 text-gray-800">
              RM {monthlyArrears.nonActive.toFixed(2)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AirArrears;
